import React, { useEffect, useState } from "react";

// components
import Table from "../Table";
import { Button, Input, Popconfirm, Select, Space } from "antd";
import { DeleteOutlined, EditOutlined, FileAddOutlined } from "@ant-design/icons";

// hooks & libs
import { useMutation } from "react-query";

// styles
import * as S from "./styles";
import { isEmpty } from "lodash-es";
import { Common } from "@secondcloset/types";
import {
  deleteFreightcomCredentials,
  insertFreightcomCredentials,
  updateFreightcomCredentials,
} from "../../../../../api/accounts/organization_freightcom";
import { FacilityCode } from "@secondcloset/fulfillment-utils";
import { useFacilityIndex } from "hooks/api/common/useFacilityIndex";

type FreightcomVersion = "v1" | "v2";

export type FreightcomCredential = {
  id?: string;
  username?: string;
  password?: string;
  organization_id: string;
  token?: string;
  facility?: FacilityCode | "all";
};
interface Props {
  freightcomCredentials?: FreightcomCredential[];
  organizationID: string;
  loading: boolean;
  refetchFreightcomCredentials: () => void;
}

const FreightcomCredentialsTable: React.FC<Props> = ({
  freightcomCredentials,
  organizationID,
  loading,
  refetchFreightcomCredentials,
}) => {
  const facilityIndex = useFacilityIndex();
  const defaultCredential: FreightcomCredential = {
    organization_id: organizationID,
  };

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formCredential, setFormCredential] = useState<FreightcomCredential>(defaultCredential);
  const [freightcomVersion, setFreightcomVersion] = useState<FreightcomVersion>("v2");
  const [currentFreightcomCredentials, setCurrentFreightcomCredentials] = useState<FreightcomCredential[]>([]);
  const [availableFacilities, setAvailableFacilities] = useState<string[]>([]);

  useEffect(() => {
    setCurrentFreightcomCredentials(freightcomCredentials || []);
    setAvailableFacilities(getFilteredFacilities()?.map((facility) => facility.name) || []);
    // eslint-disable-next-line
  }, [freightcomCredentials]);

  const { mutateAsync: insertOrganizationFreightcom, isLoading: isFreightcomInserting } = useMutation(
    insertFreightcomCredentials,
    {
      onSuccess: async (newCredential) => {
        refetchFreightcomCredentials();
        handleCloseModal();
      },
    }
  );

  const { mutateAsync: updateOrganizationFreightcom, isLoading: isFreightcomUpdating } = useMutation(
    updateFreightcomCredentials,
    {
      onSuccess: async (newCredential) => {
        refetchFreightcomCredentials();
        handleCloseModal();
      },
    }
  );

  const { mutateAsync: deleteOrgConfig, isLoading: isDeleting } = useMutation(deleteFreightcomCredentials, {
    onSuccess: () => {
      refetchFreightcomCredentials();
    },
  });

  const handleCloseModal = () => {
    // reset states
    setFreightcomVersion("v2");
    setIsModalVisible(false);
    setFormCredential(defaultCredential);
  };

  const handleDeleteClick = (freightcomCredential: FreightcomCredential) => {
    if (freightcomCredential && freightcomCredential.id) {
      deleteOrgConfig(freightcomCredential.id);
    }
  };

  const handleSubmitCredential = () => {
    switch (freightcomVersion) {
      case "v1":
        if (formCredential.username && formCredential.password && formCredential.facility) {
          updateOrInsertFreightcomCredentials();
        }
        break;
      case "v2":
        if (formCredential.token && formCredential.facility) {
          updateOrInsertFreightcomCredentials();
        }
        break;
    }
  };

  const updateOrInsertFreightcomCredentials = () => {
    if (formCredential.id) {
      updateOrganizationFreightcom(formCredential);
    } else {
      if (formCredential.facility === "all") {
        for (const facilityCode of availableFacilities) {
          const facilityToInsert = {
            ...formCredential,
            facility: facilityCode as Common.FacilityCode,
          };
          insertOrganizationFreightcom(facilityToInsert);
        }
      } else {
        insertOrganizationFreightcom(formCredential);
      }
    }
  };

  const parseFacilityTitle = (facility: Common.Facility<"V2">) =>
    `${facility.address.city} (${facility.name.toUpperCase()})`;

  const getFilteredFacilities = () => {
    return facilityIndex?.data?.filter((facility) => {
      if (currentFreightcomCredentials.length === 0) {
        return true;
      }
      const facilityAlreadyAssigned = currentFreightcomCredentials.find(
        (credential) => credential.facility === facility.name
      );
      return facilityAlreadyAssigned?.facility !== facility.name;
    });
  };

  const renderFacilityOptions = () => {
    const filteredFacilities = getFilteredFacilities();
    const facilitySelects = filteredFacilities?.map((f) => (
      <Select.Option key={f.id} value={f.name}>
        {parseFacilityTitle(f)}
      </Select.Option>
    ));
    //New facilities only allow mass updates
    if (!formCredential.id) {
      facilitySelects?.unshift(
        <Select.Option key={"all"} value={"all"}>
          {"All Unassigned Facilities"}
        </Select.Option>
      );
    }
    return facilitySelects;
  };

  const columns = [
    {
      title: "Facility",
      dataIndex: "facility",
      key: "facility",
      width: "33%",
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: "33%",
    },
    {
      title: "Token",
      dataIndex: "token",
      key: "token",
      width: "33%",
    },
    {
      title: "Action",
      dataIndex: "",
      key: "",
      render(_: unknown, record: FreightcomCredential) {
        return (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => {
                setFormCredential(record);
                if (record.username) {
                  setFreightcomVersion("v1");
                } else if (record.token) {
                  setFreightcomVersion("v2");
                }
                setIsModalVisible(true);
              }}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure to delete this credential?"
              onConfirm={(e) => handleDeleteClick(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" icon={<DeleteOutlined />} loading={isDeleting} danger>
                Remove
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        tableProps={{
          columns,
          dataSource: isEmpty(currentFreightcomCredentials) ? null : currentFreightcomCredentials,
          loading,
        }}
        title="Freightcom Credentials"
        headerButtons={
          <Button icon={<FileAddOutlined />} onClick={() => setIsModalVisible(true)} disabled={loading}>
            Add Credential
          </Button>
        }
      />
      <S.Modal
        visible={isModalVisible}
        title={"Freightcom Credentials"}
        onCancel={handleCloseModal}
        onOk={handleSubmitCredential}
        okText="Submit"
        confirmLoading={isFreightcomInserting || isFreightcomUpdating}
      >
        <Space direction="vertical">
          <Select
            value={freightcomVersion}
            style={{ width: "100%" }}
            onChange={(version) => {
              setFreightcomVersion(() => version);
              setFormCredential((state) => {
                switch (version) {
                  case "v1":
                    return {
                      ...state,
                      token: "",
                    };
                  case "v2":
                    return {
                      ...state,
                      username: "",
                      password: "",
                    };
                }
              });
            }}
          >
            <Select.Option key={"v1"} value={"v1"}>
              {"Freightcom Version 1"}
            </Select.Option>
            <Select.Option key={"v2"} value={"v2"}>
              {"Freightcom Version 2"}
            </Select.Option>
          </Select>
          {freightcomVersion === "v1" && (
            <>
              <Input
                type="text"
                placeholder="Username"
                value={formCredential?.username ?? ""}
                onChange={(e) =>
                  setFormCredential((state) => ({
                    ...state,
                    username: e.target.value,
                  }))
                }
              />
              <Input
                type="password"
                placeholder="Password"
                value={formCredential?.password ?? ""}
                onChange={(e) =>
                  setFormCredential((state) => ({
                    ...state,
                    password: e.target.value,
                  }))
                }
              />
            </>
          )}
          {freightcomVersion === "v2" && (
            <>
              <Input
                type="token"
                placeholder="Token"
                hidden={freightcomVersion !== "v2"}
                value={formCredential?.token ?? ""}
                onChange={(e) =>
                  setFormCredential((state) => ({
                    ...state,
                    token: e.target.value,
                  }))
                }
              />
            </>
          )}
          <Select
            style={{ width: "100%" }}
            placeholder="Facility"
            value={formCredential?.facility}
            onChange={(e: Common.FacilityCode | "all") =>
              setFormCredential((state) => {
                if (state) {
                  return {
                    ...state,
                    facility: e,
                  };
                }
                return state;
              })
            }
          >
            {renderFacilityOptions()}
          </Select>
        </Space>
      </S.Modal>
    </>
  );
};

export default FreightcomCredentialsTable;
